import styled from 'styled-components';
import { up } from '@/themes';
import Reveal from './reveal';
import { CardTitle, Text, VisuallyHidden } from './typography';

const Panel = styled.div`
  --node: 44px;

  background-color: color-mix(in oklab, var(--muted) 40%, transparent);
  border-radius: var(--radius-3xl);
  box-shadow:
    0 0 0 1px var(--border),
    inset 0 2px 12px rgb(0 0 0 / 0.06);
  padding: 24px;

  ${up.sm} {
    padding: 40px;
  }

  ${up.lg} {
    --node: 48px;
  }

  .dark & {
    box-shadow:
      0 0 0 1px var(--border),
      inset 0 2px 12px rgb(0 0 0 / 0.4);
  }
`;

const List = styled.ol`
  display: grid;
  gap: 32px;
  list-style: none;
  margin: 0;
  padding: 0;

  ${up.lg} {
    gap: 24px;
    grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  }
`;

const Item = styled.li`
  position: relative;
`;

const Row = styled(Reveal)`
  display: flex;
  gap: 16px;

  ${up.lg} {
    flex-direction: column;
    gap: 20px;
  }
`;

const Node = styled.span`
  align-items: center;
  background-image: var(--gradient-brand);
  border-radius: 9999px;
  box-shadow:
    0 0 0 2px var(--card),
    0 10px 15px -3px color-mix(in oklab, var(--primary) 25%, transparent),
    0 4px 6px -4px color-mix(in oklab, var(--primary) 25%, transparent);
  color: var(--primary-foreground);
  display: inline-flex;
  flex-shrink: 0;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  height: var(--node);
  justify-content: center;
  /* Sits above the connector running out of the previous step. */
  position: relative;
  width: var(--node);
  z-index: 1;

  ${up.lg} {
    font-size: 18px;
  }
`;

const Copy = styled.div`
  min-width: 0;
  padding-top: 8px;

  ${up.lg} {
    padding-top: 0;
  }
`;

const Title = styled(CardTitle)`
  text-wrap: balance;
`;

const Description = styled(Text)`
  color: var(--muted-foreground);
  margin-top: 6px;
  text-wrap: pretty;
`;

const Connector = styled.span`
  /* Stacked: runs from under the node down into the next step's gap. */
  background-image: linear-gradient(
    to bottom,
    color-mix(in oklab, var(--brand-pink) 70%, transparent),
    color-mix(in oklab, var(--brand-indigo) 70%, transparent)
  );
  border-radius: 9999px;
  bottom: -24px;
  left: calc(var(--node) / 2);
  pointer-events: none;
  position: absolute;
  top: calc(var(--node) + 8px);
  transform: translateX(-50%);
  width: 3px;

  /* Row: runs from beside the node to just before the next one. */
  ${up.lg} {
    background-image: linear-gradient(
      to right,
      color-mix(in oklab, var(--brand-pink) 70%, transparent),
      color-mix(in oklab, var(--brand-indigo) 70%, transparent)
    );
    bottom: auto;
    height: 3px;
    left: calc(var(--node) + 8px);
    right: -16px;
    top: calc(var(--node) / 2);
    transform: translateY(-50%);
    width: auto;
  }
`;

/**
 * Numbered "how it works" timeline on an inset card: gradient number nodes
 * joined by a pink-to-indigo connector. Stacked below 1024px, a row above.
 *
 *   <StepList
 *     steps={[
 *       { title: 'Pick a gift card', description: 'Choose from 4,700+ brands.' },
 *       { title: 'Pay', description: 'Check out in a couple of minutes.' },
 *       { title: 'Get your code', description: 'It arrives by email.' },
 *     ]}
 *   />
 *
 * Works best with 3 to 5 steps.
 */
const StepList = ({ className, steps = [] }) => {
  const last = steps.length - 1;
  const columns = Math.min(Math.max(steps.length, 3), 5);

  return (
    <Panel className={className}>
      <List $columns={columns}>
        {steps.map((step, index) => (
          <Item key={step.title}>
            <Row delay={index * 80}>
              <Node aria-hidden="true">{index + 1}</Node>
              <Copy>
                <Title>
                  <VisuallyHidden>{index + 1}. </VisuallyHidden>
                  {step.title}
                </Title>
                <Description>{step.description}</Description>
              </Copy>
            </Row>
            {index < last && <Connector aria-hidden="true" />}
          </Item>
        ))}
      </List>
    </Panel>
  );
};

export default StepList;
